import React, { useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import { usePostResendMutation, useStatusQuery } from 'services/freeTrial';
import {
  useFreeTrialContext,
  useFreeTrialPageNavigation,
} from 'providers/FreeTrialProvider';
import { useSnackbarForMessage } from 'providers/SnackbarProvider';
import {
  LogoImage,
  PageTitle,
  PageSubtitle,
  PageBlurb,
} from 'features/freeTrial';
import Box from '@mui/material/Box';
import Button from 'components/Button';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';

export default function CheckYourEmailPage() {
  const [polling, setPolling] = useState(true);
  const { state, meta } = useFreeTrialContext();
  const { announceSnackbar } = useSnackbarForMessage();
  const page = meta.pages.BASIC_INFO_FORM;
  const userEmail = state?.formBag[page]?.email || 'your email';

  const [postResend, { isLoading: isResending }] = usePostResendMutation();

  const query = useStatusQuery(
    { registrationToken: state?.registrationToken },
    { pollingInterval: polling ? 5000 : false }
  );

  useHandleRedirection(query, () => {
    setPolling(false);
  });

  const handleResend = async (event) => {
    event.preventDefault();
    try {
      await postResend({
        body: { registrationToken: state?.registrationToken },
      }).unwrap();

      announceSnackbar("We've resent the email.", 'success');
    } catch (error) {
      console.log(`%cError: Check Your Email Page`, 'color: red');
      console.error(error);
      announceSnackbar(
        `Unable to resend email. Please contact support for assistance.`,
        'error'
      );
    }
  };

  return (
    <Content>
      <Stack direction="row" alignItems="center" gap={2}>
        <LogoImage />
        <PageTitle>Check Your Email</PageTitle>
      </Stack>
      <Divider sx={{ mt: 2, mb: 4 }} />
      <Stack gap={2}>
        <PageSubtitle>
          A validation email from <span>no-reply@swipeclock.com</span> has been
          sent to <span>{userEmail}</span>.
        </PageSubtitle>
        <PageSubtitle>
          Click the link in the email to verify your free trial setup.
        </PageSubtitle>
      </Stack>
      <PageBlurb sx={{ mt: 4 }}>
        Didn't receive an email? Look in your spam folder or
        <Button
          variant="text"
          endIcon={<div />}
          loading={isResending}
          loadingPosition="end"
          onClick={handleResend}
          // Hack to get the loading indicator to show up in the right place
          // position end normally requires an icon, but we don't want an icon
          sx={{
            '& .MuiLoadingButton-loadingIndicator': {
              right: '-16px',
              bottom: '12px',
            },
          }}
        >
          Resend the email.
        </Button>
      </PageBlurb>
    </Content>
  );
}

// ------------------------------------------------------------- //
// Redirection
// Handles various redirection scenarios based on the trial status.
// ------------------------------------------------------------- //
function useHandleRedirection(query, stopPolling) {
  const { meta } = useFreeTrialContext();
  const navigateToPage = useFreeTrialPageNavigation();
  const hasRedirected = useRef(false);

  const { isSuccess, isError, data: statusMessage } = query;
  const { announceSnackbar } = useSnackbarForMessage();

  const cleanup = () => {
    hasRedirected.current = false;
  };

  useEffect(() => {
    // Guard against multiple redirects
    if (hasRedirected.current) {
      return cleanup();
    }

    let pageToNavigate = null;

    if (isError) {
      pageToNavigate = meta.pages.ERROR_DEFAULT;
      announceSnackbar(
        `Something went wrong. Please contact support for assistance.`,
        'error'
      );
    } else if (isSuccess) {
      switch (statusMessage) {
        case meta.status.SITE_CREATED:
          pageToNavigate = meta.pages.ERROR_SITE_ALREADY_CREATED;
          break;
        case meta.status.EMAIL_CONFIRMED:
          pageToNavigate = meta.pages.BASIC_INFO_FORM;
          break;
        // If the status is anything else, we just keep polling
        default:
          break;
      }
    }

    // Finally found a page to navigate to
    if (pageToNavigate) {
      hasRedirected.current = true;
      stopPolling();
      navigateToPage(pageToNavigate);
    }

    // If no if statements are true, we just keep polling :)
    return cleanup();
  }, [statusMessage, isSuccess, isError]);
}

// ------------------------------------------------------------- //
// Styles and components
// ------------------------------------------------------------- //
const Content = styled(Box)(({ theme }) => ({
  width: 'max-content',
  margin: `${theme.spacing(4)} auto`,
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(0),
  },
  [theme.breakpoints.down('md')]: {
    width: 'auto',
  },
}));
